import { h } from 'preact';
import { useContext } from 'preact/hooks';
import classnames from 'classnames';
import queryString from 'query-string';
import { SearchContext } from '../contexts/Search';
import Skeleton from './Skeleton';

const { __ } = wp.i18n;
const SEPARATOR = '&hellip;';

/**
 * Range of numbers.
 *
 * @param {Int} start Starting value.
 * @param {Int} end Ending value.
 * @returns {Array} Array of numbers from start to end.
 */
const range = ( start, end ) => {
	const length = end - start + 1;
	return Array.from( { length }, ( _, index ) => index + start );
};

/**
 * Get the pagination array.
 *
 * @param {Int} currentPage The current page.
 * @param {Int} totalCount  The total items for the query.
 * @param {Int} siblingCount The amount of siblings to show on either side of the current page.
 * @param {Int} pageSize The amount of items on a page.
 * @returns {Array} Array of the pagination items.
 */
const getPaginationArray = ( currentPage, totalCount, siblingCount, pageSize ) => {

	const totalPageCount = Math.ceil( totalCount / pageSize );

	// Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
	const totalPageNumbers = siblingCount + 5;

	// If the number of pages is less than the page numbers we want to show in our paginationComponent, we return the range [1..totalPageCount]
	if ( totalPageNumbers >= totalPageCount ) {
		return range( 1, totalPageCount );
	}

	const leftSiblingIndex = Math.max( currentPage - siblingCount, 1 );
	const rightSiblingIndex = Math.min( currentPage + siblingCount, totalPageCount );

	/**
	 * We do not want to show dots if there is only one position left
	 * after/before the left/right page count as that would lead to a change if our Pagination
	 * component size which we do not want
	 */
	const shouldShowLeftDots = 2 < leftSiblingIndex;
	const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

	const firstPageIndex = 1;
	const lastPageIndex = totalPageCount;

	if ( !shouldShowLeftDots && shouldShowRightDots ) {
		const leftItemCount = 3 + 2 * siblingCount;
		const leftRange = range( 1, leftItemCount );

		return [...leftRange, SEPARATOR, totalPageCount];
	}

	if ( shouldShowLeftDots && !shouldShowRightDots ) {
		const rightItemCount = 3 + 2 * siblingCount;
		const rightRange = range(
			totalPageCount - rightItemCount + 1,
			totalPageCount
		);
		return [firstPageIndex, SEPARATOR, ...rightRange];
	}

	if ( shouldShowLeftDots && shouldShowRightDots ) {
		const middleRange = range( leftSiblingIndex, rightSiblingIndex );
		return [firstPageIndex, SEPARATOR, ...middleRange, SEPARATOR, lastPageIndex];
	}
};

/**
 * Get the href attribute for the link.
 *
 * @param {Object} params
 * @param {Int} page
 * @returns {String} Href value to use.
 */
const getHref = ( params, page ) => {
	// Make a copy of the params
	const paramsCopy = { ...params };

	// Remove the page key.
	// This is because in the actual URL, "pg" is used, and we don't want both.
	delete paramsCopy.page;

	const payloads = [ 'onload', 'onerror', 'svg', '<script', 'javascript:', 'alert(', 'prompt(', 'confirm(', '<x', '<img'];

	for ( let i = 0; i < payloads.length; i++ ) {
		if ( JSON.stringify( paramsCopy ).includes( payloads[i] ) ) {
			// Loop through all of paramsCopy and remove any payloads
			for ( let j = 0; j < Object.keys( paramsCopy ).length; j++ ) {
				if ( JSON.stringify( paramsCopy[Object.keys( paramsCopy )[j]] ).includes( payloads[i] ) ) {
					delete paramsCopy[Object.keys( paramsCopy )[j]];
				}
			}
		}
	}

	// Make a clean query string.
	const query = queryString.stringify( { ...paramsCopy, pg: page }, { arrayFormat: 'bracket' } );

	// Get window path info.
	const { origin, pathname } = window.location;

	// Put it all together.
	const href = `${origin}${pathname}?${query}`;

	return href;
};

/**
 * The main component.
 *
 * @param {Object} props Props
 * @return {Object} JSX
 */
const Pagination = () => {
	const { state, helpers } = useContext( SearchContext );
	const { update } = helpers;
	const { maxNumPages, page: currentPage, postsPerPage: pageSize, foundPosts: totalCount, params } = state;
	const paginationRange = getPaginationArray( currentPage, totalCount, 1, pageSize );

	if ( 0 === currentPage || 2 > paginationRange.length ) {
		return null;
	}

	/**
	 * Handle the page click.
	 * @param {Object} event The event object.
	 * @param {Int} page The page to go to.
	 */
	const handlePageClick = ( event, page ) => {
		event.preventDefault();

		const updatedParams = {
			...params,
			page
		};

		const payloads = [ 'onload', 'onerror', 'svg', '<script', 'javascript:', 'alert(', 'prompt(', 'confirm(', '<x', '<img'];

		for ( let i = 0; i < payloads.length; i++ ) {
			if ( JSON.stringify( updatedParams ).includes( payloads[i] ) ) {
				// Loop through all of paramsCopy and remove any payloads
				for ( let j = 0; j < Object.keys( updatedParams ).length; j++ ) {
					if ( JSON.stringify( updatedParams[Object.keys( updatedParams )[j]] ).includes( payloads[i] ) ) {
						delete updatedParams[Object.keys( updatedParams )[j]];
					}
				}
			}
		}

		// Update
		update( updatedParams );
	};

	return (
		<div className="faceted-search-results__pagination d-flex justify-content-center py-5" data-bi-aN="pagination">
			<Skeleton className="mb-3">
				<nav
					aria-label={__( `Pagination for search results: ${maxNumPages} pages`, 'ms-research' )}
					role="navigation">
					<ul className="pagination pagination-segment-middle">

						<li className={classnames( 'page-item', { disabled: 1 === currentPage } )}>
							<a
								onClick={( event ) => handlePageClick( event, currentPage - 1 )}
								aria-label={__( 'Previous page', 'ms-research' )}
								className="page-link"
								data-bi-cn={__( 'Previous', 'ms-research' )}
								href={getHref( params, currentPage - 1 )}>
								{__( 'Previous', 'ms-research' )}
							</a>
						</li>

						{paginationRange.map( ( item, index ) => {
							if ( item === SEPARATOR ) {
								return (
									<li key={index} className="page-item disabled ellipsis">
										<span className="page-link text-body" dangerouslySetInnerHTML={{ __html: SEPARATOR }}></span>
									</li>
								);
							}

							const isActive = item === currentPage;
							const ariaProps = {
								'aria-label': __( 'Link to page %s', item ),
							};

							if ( isActive ) {
								ariaProps['aria-current'] = 'page';
							}

							return (
								<li
									key={index}
									className={classnames( 'page-item', {
										active: isActive
									} )}>
									<a
										onClick={( event ) => handlePageClick( event, item )}
										className="page-link"
										data-bi-cn={__( 'Page %s', item )}
										href={getHref( params, item )}
										{...ariaProps}>
										{item}
									</a>
								</li>
							);
						} )}

						<li className={classnames( 'page-item', { disabled: currentPage === maxNumPages } )}>
							<a
								onClick={( event ) => handlePageClick( event, currentPage + 1 )}
								aria-label={__( 'Next page', 'ms-research' )}
								className="page-link"
								data-bi-cn={__( 'Next', 'ms-research' )}
								href={getHref( params, currentPage + 1 )}>
								{__( 'Next', 'ms-research' )}
							</a>
						</li>

					</ul>
				</nav>
			</Skeleton>
		</div>
	);
};

export default Pagination;
